import { useState } from 'react'
import { Database, Download, Plus } from 'lucide-react'
import { Link } from 'react-router-dom'

import type { RoleCode } from '@cattle/money'

import {
  Avatar,
  Banner,
  Button,
  DataTable,
  EmptyState,
  LoadingSkeleton,
  LockChip,
  Money,
  PageHeader,
  PinSheet,
  QueryBoundary,
  Section,
  StatTile,
  StatusBadge,
  usePriceLock,
  useToast,
  type Column,
  type StatusTone,
} from '../../../components'
import { useOwnerOverview, type ActivityEntry, type Person } from '../../../fixtures'
import { formatLongDate } from '../../../lib/format'
import { t } from '../../../lib/i18n'
import { routes } from '../../../lib/routes'

/**
 * O-01 · Overview — the trader's own first screen.
 *
 * The backup banner carries its own action INLINE: a banner he has to navigate
 * away from is a banner he will dismiss. Every money figure on this page —
 * including the Was/Now columns of the activity table — goes through `gate()`,
 * so Price Lock is `undefined` in and `••••••` out rather than a CSS blur.
 */

/** The deck's badge tones: Owner reads ok, Partner warn, Staff neutral. */
const ROLE_TONE: Record<RoleCode, StatusTone> = {
  OWNER: 'ok',
  PARTNER: 'warn',
  STAFF: 'neutral',
}

/** Seven days is the line: older than that and the warning appears. */
const BACKUP_WARN_DAYS = 7

export function OwnerOverviewScreen() {
  const overview = useOwnerOverview()
  const toast = useToast()
  const { gate } = usePriceLock()
  const [pinOpen, setPinOpen] = useState(false)

  /**
   * Was and Now are the whole point of an audit row. A cell carries money, or
   * text, or neither — and money is gated exactly as a tile is.
   */
  const changeCell = (paisa: number | undefined, text: string | undefined, deleted: boolean) => {
    if (paisa !== undefined) return <Money paisa={gate(paisa)} />
    if (text) {
      return (
        <span className={deleted ? 'font-display font-bold text-danger' : 'text-dark'}>{text}</span>
      )
    }
    return <span className="text-light">{t('common.none')}</span>
  }

  const columns: Column<ActivityEntry>[] = [
    {
      id: 'who',
      header: t('label.who'),
      cell: (entry) => (
        <div className="flex items-center gap-2.5">
          <Avatar name={entry.person} size="sm" />
          <div className="min-w-0">
            <div className="font-semibold text-dark">{entry.person}</div>
            <div className="text-[11.5px] text-muted">{entry.at}</div>
          </div>
        </div>
      ),
    },
    {
      id: 'what',
      header: t('o.overview.colWhatChanged'),
      cell: (entry) => <span className="text-sub">{entry.what}</span>,
    },
    {
      id: 'was',
      header: t('label.was'),
      numeric: true,
      cell: (entry) => changeCell(entry.wasPaisa, entry.wasText, false),
    },
    {
      id: 'now',
      header: t('label.now'),
      numeric: true,
      cell: (entry) => changeCell(entry.nowPaisa, entry.nowText, entry.kind === 'DELETED'),
    },
  ]

  const peopleRow = (person: Person) => (
    <div key={person.id} className="flex items-center gap-3 py-[9px]">
      <Avatar name={person.name} />
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-semibold text-dark">{person.name}</div>
        <div className="text-[11.5px] text-muted">
          {person.isYou ? t('o.overview.signedInNow') : person.lastSignedIn}
        </div>
      </div>
      <StatusBadge tone={ROLE_TONE[person.role]}>{t(`role.${person.role}`)}</StatusBadge>
    </div>
  )

  return (
    <QueryBoundary
      query={overview}
      skeleton={
        <>
          <LoadingSkeleton variant="tiles" className="mb-3.5" />
          <div className="grid gap-3.5 lg:grid-cols-[1.55fr_1fr]">
            <LoadingSkeleton variant="table" rows={4} />
            <LoadingSkeleton variant="list" rows={3} />
          </div>
        </>
      }
    >
      {(data) => (
        <>
          <PageHeader
            title={t('o.overview.greeting', { name: data.ownerName })}
            subtitle={formatLongDate(data.today)}
            actions={
              <>
                <LockChip onRequestUnlock={() => setPinOpen(true)} />
                <Button
                  variant="primary"
                  icon={<Plus className="size-4" />}
                  onClick={() => toast.show(t('o.overview.addAnimal'), 'info')}
                >
                  {t('o.overview.addAnimal')}
                </Button>
              </>
            }
          />

          {/* Never backed up is a different sentence from "backed up a while
              ago" — and it is the more alarming one, so it says so plainly
              rather than printing a day count nobody can act on. */}
          {data.lastBackupDaysAgo === null || data.lastBackupDaysAgo > BACKUP_WARN_DAYS ? (
            <Banner
              tone="warn"
              icon={<Database className="size-5" />}
              title={
                data.lastBackupDaysAgo === null
                  ? t('o.overview.backupNeverTitle')
                  : t('o.overview.backupWarnTitle', { days: data.lastBackupDaysAgo })
              }
              body={
                data.lastBackupDaysAgo === null
                  ? t('o.overview.backupNeverBody')
                  : t('o.overview.backupWarnBody', { days: data.lastBackupDaysAgo })
              }
              action={
                <Button
                  variant="primary"
                  icon={<Download className="size-4" />}
                  onClick={() => toast.show(t('o.overview.backupNow'), 'info')}
                >
                  {t('o.overview.backupNow')}
                </Button>
              }
            />
          ) : null}

          <div className="mb-3.5 grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
            <StatTile
              surface="heavy"
              label={t('o.overview.inStock')}
              value={<span className="tnum">{data.inStock}</span>}
              sub={t('o.overview.inStockSub', { count: data.soldThisSeason })}
            />
            <StatTile
              surface="heavy"
              label={t('o.overview.moneyTiedUp')}
              value={<Money short paisa={gate(data.moneyTiedUpPaisa)} />}
              sub={t('o.overview.moneyTiedUpSub')}
            />
            <StatTile
              surface="heavy"
              label={t('o.overview.costingYou')}
              value={
                <span className="inline-flex items-baseline gap-0.5">
                  <Money tone="warn" paisa={gate(data.dailyCostPaisa)} />
                  <small className="text-[13px] font-bold text-muted">{t('unit.perDay')}</small>
                </span>
              }
              sub={t('o.overview.costingYouSub')}
            />
            <StatTile
              surface="heavy"
              label={t('o.overview.profitBooked')}
              value={<Money signed tone="positive" paisa={gate(data.profitBookedPaisa)} />}
              sub={t('o.overview.profitBookedSub', {
                count: data.soldThisSeason,
                days: data.profitBookedDaysHeld,
              })}
            />
          </div>

          <div className="grid gap-3.5 lg:grid-cols-[1.55fr_1fr]">
            <Section
              title={t('o.overview.recentActivity')}
              action={
                <Link
                  to={routes.owner.history()}
                  className="text-[12.5px] font-bold text-accent-dark"
                >
                  {t('act.viewAll')}
                </Link>
              }
            >
              <DataTable
                columns={columns}
                rows={data.activity.slice(0, 4)}
                rowKey={(entry) => entry.id}
                caption={t('o.overview.recentActivity')}
                empty={
                  <EmptyState
                    title={t('o.overview.emptyTitle')}
                    body={t('o.overview.emptyBody')}
                    action={
                      <Button
                        variant="primary"
                        icon={<Plus className="size-4" />}
                        onClick={() => toast.show(t('o.overview.addAnimal'), 'info')}
                      >
                        {t('o.overview.addAnimal')}
                      </Button>
                    }
                  />
                }
              />
            </Section>

            <Section
              title={t('o.people.title')}
              action={
                <Link
                  to={routes.owner.people()}
                  className="text-[12.5px] font-bold text-accent-dark"
                >
                  {t('act.manage')}
                </Link>
              }
            >
              <div className="flex flex-col">{data.people.map(peopleRow)}</div>
              <p className="mt-3 border-t border-muted/15 pt-3 text-xs leading-relaxed text-muted">
                {t('o.overview.peopleNote')}
              </p>
            </Section>
          </div>

          <PinSheet open={pinOpen} onClose={() => setPinOpen(false)} />
        </>
      )}
    </QueryBoundary>
  )
}
